import type { MerchantCurrencyCode } from "@/utils/currency"
import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"

export const ENGAGEMENT_REPORT_QUERY_KEY =
  "engagement_reports"
export type EngagementReport = {
  tenant: number
  merchantCategoryCode: string | null
  merchantSector: string | null

  forMonthStartingOn: Date

  transactionScore: number
  engagementScore: number
  reportingScore: number
  portalScore: number
  featureScore: number
  productScore: number

  engagementScoreTpAvg: number
  reportingScoreTpAvg: number
  portalScoreTpAvg: number
  featureScoreTpAvg: number
  productScoreTpAvg: number

  engagementScoreMccAvg: number
  reportingScoreMccAvg: number
  portalScoreMccAvg: number
  featureScoreMccAvg: number
  productScoreMccAvg: number

  engagementScoreSectorAvg: number
  reportingScoreSectorAvg: number
  portalScoreSectorAvg: number
  featureScoreSectorAvg: number
  productScoreSectorAvg: number

  totalTransactions: number
  averageTransactionValue: number
  averageWeeklyTransactions: number
  hasViewedPortalReports: boolean
  hasViewedDeviceReports: boolean
  hasExportedTransactions: boolean
  hasImportedProducts: boolean
  hasEditedPortalConfig: boolean
  hasViewedMultipleReportTypes: boolean
  hasViewedPortalSummary: boolean
  countOfCashiers: number
  countOfProducts: number
  countOfCategories: number
  countOfOrders: number
  percentageOfOrdersUsingProducts: number
  percentageOfOrdersUsingCashier: number
  percentageOfOrdersUsingNotes: number
  percentageOfOrdersUsingTable: number
  percentageOfOrdersUsingSplitPayment: number
  percentageOfOrdersUsingCash: number
  percentageOfOrdersUsingGratuity: number
  percentageOfOrdersUsingServiceCharge: number
  percentageOfOrdersUsingCollection: number
  percentageOfOrdersUsingOpenOrder: number
  countOfUnlinkedRefunds: number

  merchantCurrency: MerchantCurrencyCode
}

export type EngagementReportQuery = {
  forMonthStartingOn: string
}

export const retrieveEngagementReport = async (
  tenantId: number,
  query?: EngagementReportQuery
) => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<EngagementReport>
  >(`/merchants/${tenantId}/engagement/`, { query })

  return {
    ...body,
    forMonthStartingOn: DateFns.parseISO(
      body.forMonthStartingOn
    ),
  }
}

export const useEngagementReport = (
  tenantId: MaybeRef<number>,
  query?: MaybeRef<EngagementReportQuery>
) =>
  useQuery({
    queryKey: [
      ENGAGEMENT_REPORT_QUERY_KEY,
      tenantId,
      query,
    ] as const,
    queryFn: ({ queryKey: [_, id, query_] }) =>
      retrieveEngagementReport(id, query_),
  })
